'use client';

import { useCallback, useEffect, useState } from 'react';
import { Gift, LogOut, Phone, ShieldCheck, Sparkles, UserRound } from 'lucide-react';

export type PublicCustomer = {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  order_count?: number;
  points_balance?: number;
  lifetime_points?: number;
  tier?: string;
  stamps?: number;
  marketing_opt_in?: boolean;
};

type Mode = 'idle' | 'otp' | 'pin' | 'profile';
type Intent = 'login' | 'register';

const TIER_LABELS: Record<string, string> = {
  bronze: 'Bronze',
  silver: 'Prata',
  gold: 'Ouro',
  platinum: 'Platina',
};

type Props = {
  enabled?: boolean;
  apiQuery?: string;
  compact?: boolean;
  /** When false, show a compact Entrar / Criar conta strip until the user expands. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onAuthenticated?: (customer: PublicCustomer | null) => void;
};

export function CustomerAuthPanel({
  enabled = true,
  apiQuery = '',
  compact = false,
  open,
  onOpenChange,
  onAuthenticated,
}: Props) {
  const [crmEnabled, setCrmEnabled] = useState(enabled);
  const [customer, setCustomer] = useState<PublicCustomer | null>(null);
  const [mode, setMode] = useState<Mode>('idle');
  const [intent, setIntent] = useState<Intent>('login');
  const [internalOpen, setInternalOpen] = useState(Boolean(open));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    code: '',
    pin: '',
  });

  const expanded = open ?? internalOpen;
  const setExpanded = (next: boolean, nextIntent?: Intent) => {
    if (nextIntent) setIntent(nextIntent);
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const endpoint = `/api/public/customer${apiQuery}`;

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(endpoint, { cache: 'no-store' });
      const data = await res.json();
      if (res.status === 403) {
        setCrmEnabled(false);
        setCustomer(null);
        onAuthenticated?.(null);
        return;
      }
      setCrmEnabled(true);
      if (data.authenticated && data.customer) {
        setCustomer(data.customer);
        onAuthenticated?.(data.customer);
      } else {
        setCustomer(null);
        onAuthenticated?.(null);
      }
    } catch {
      setCustomer(null);
    }
  }, [endpoint, onAuthenticated]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    if (open !== undefined) setInternalOpen(open);
  }, [open]);

  if (!enabled || !crmEnabled) return null;

  const post = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Falha no acesso.');
      if (data.debug_code) setMessage(`Codigo: ${data.debug_code}`);
      else if (data.message) setMessage(data.message);
      if (data.customer) {
        setCustomer(data.customer);
        onAuthenticated?.(data.customer);
        setMode('idle');
        setForm((current) => ({ ...current, code: '', pin: '' }));
        setExpanded(false);
      }
      if (body.action === 'logout') {
        setCustomer(null);
        onAuthenticated?.(null);
        setMode('idle');
        setExpanded(false);
      }
      if (body.action === 'request_otp') setMode('otp');
      return data;
    } catch (err) {
      setError((err as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  if (customer && mode === 'idle' && !expanded) {
    return (
      <section
        id="customer-auth"
        className={`flex items-center justify-between gap-3 rounded-2xl border border-surface-3 bg-surface-0 ${compact ? 'p-3' : 'p-3.5'}`}
      >
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => setExpanded(true)}
          aria-expanded="false"
        >
          <p className="truncate text-sm font-semibold text-ink-900">{customer.name}</p>
          <p className="mt-0.5 text-xs text-ink-500">
            {customer.points_balance ?? 0} pts · {TIER_LABELS[customer.tier || 'bronze'] || customer.tier}
          </p>
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm shrink-0"
          onClick={() => void post({ action: 'logout' })}
          aria-label="Sair da conta"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </button>
      </section>
    );
  }

  if (customer && mode === 'idle' && expanded) {
    return (
      <section id="customer-auth" className={`rounded-2xl border border-surface-3 bg-surface-0 ${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-500">A sua conta</p>
            <p className="mt-1 truncate text-lg font-semibold text-ink-900">{customer.name}</p>
            <p className="text-sm text-ink-500">{customer.phone}</p>
          </div>
          <div className="flex shrink-0 gap-1">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpanded(false)}>
              Fechar
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void post({ action: 'logout' })}
              aria-label="Sair da conta"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className={`mt-3 grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <div className="rounded-xl bg-brand-50 px-3 py-2">
            <p className="text-[11px] font-medium text-brand">Pontos</p>
            <p className="text-lg font-semibold text-ink-900">{customer.points_balance ?? 0}</p>
          </div>
          <div className="rounded-xl bg-surface-2 px-3 py-2">
            <p className="text-[11px] font-medium text-ink-500">Nivel</p>
            <p className="text-lg font-semibold text-ink-900">{TIER_LABELS[customer.tier || 'bronze'] || customer.tier}</p>
          </div>
          {!compact && (
            <div className="rounded-xl bg-surface-2 px-3 py-2">
              <p className="text-[11px] font-medium text-ink-500">Pedidos</p>
              <p className="text-lg font-semibold text-ink-900">{customer.order_count ?? 0}</p>
            </div>
          )}
        </div>
        <p className="mt-3 flex items-center gap-2 text-xs text-ink-500">
          <Sparkles className="h-3.5 w-3.5 text-brand" />
          Checkout mais rapido e atendimento personalizado.
        </p>
      </section>
    );
  }

  if (!expanded) {
    return (
      <section
        id="customer-auth"
        className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-surface-3 bg-surface-0 ${compact ? 'p-3' : 'p-3.5'}`}
      >
        <div className="min-w-0 flex items-center gap-2.5">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand">
            <UserRound className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900">Conta e pontos</p>
            <p className="text-xs text-ink-500">Encomende mais depressa e ganhe pontos.</p>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setMode('idle');
              setExpanded(true, 'login');
            }}
          >
            Entrar
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => {
              setMode('idle');
              setExpanded(true, 'register');
            }}
          >
            Criar conta
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="customer-auth" className={`rounded-2xl border border-surface-3 bg-surface-0 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-brand-50 text-brand">
            <Gift className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-semibold text-ink-900">
              {intent === 'register' ? 'Criar conta' : 'Entrar'}
            </h2>
            <p className="text-xs text-ink-500">
              {intent === 'register'
                ? 'Crie a conta com o telemovel e ganhe pontos em cada pedido.'
                : 'Entre para ganhar pontos e encomendar mais depressa.'}
            </p>
          </div>
        </div>
        <button type="button" className="btn btn-ghost btn-sm shrink-0" onClick={() => setExpanded(false)}>
          Fechar
        </button>
      </div>

      {mode === 'idle' && (
        <div className="space-y-2">
          {(intent === 'register' || form.name) && (
            <>
              <label className="input-label">Nome</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="O seu nome"
                autoComplete="name"
              />
            </>
          )}
          {intent === 'login' && !form.name && (
            <button
              type="button"
              className="text-sm font-medium text-brand"
              onClick={() => setIntent('register')}
            >
              Ainda nao tem conta? Criar conta
            </button>
          )}
          {intent === 'register' && (
            <button
              type="button"
              className="text-sm font-medium text-brand"
              onClick={() => setIntent('login')}
            >
              Ja tem conta? Entrar
            </button>
          )}
          <label className="input-label">Telemovel</label>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="9xx xxx xxx"
            inputMode="tel"
            autoComplete="tel"
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !form.phone.trim() || (intent === 'register' && !form.name.trim())}
              onClick={() =>
                void post({
                  action: 'request_otp',
                  phone: form.phone,
                  name: form.name || undefined,
                })
              }
            >
              <Phone className="h-4 w-4" />
              {intent === 'register' ? 'Criar com codigo' : 'Entrar com codigo'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy || !form.phone.trim()}
              onClick={() => setMode('pin')}
            >
              <ShieldCheck className="h-4 w-4" /> Tenho PIN
            </button>
          </div>
        </div>
      )}

      {mode === 'otp' && (
        <div className="space-y-2">
          <p className="text-sm text-ink-600">Introduza o codigo de 6 digitos enviado para {form.phone}.</p>
          <input
            className="input tracking-[0.3em]"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="••••••"
            inputMode="numeric"
            maxLength={6}
            autoComplete="one-time-code"
          />
          <label className="input-label">PIN rapido (opcional, 4-6 digitos)</label>
          <input
            className="input"
            value={form.pin}
            onChange={(e) => setForm({ ...form, pin: e.target.value })}
            placeholder="Para proximas visitas"
            inputMode="numeric"
            maxLength={6}
          />
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => setMode('idle')}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-primary flex-1"
              disabled={busy || form.code.length < 6}
              onClick={() =>
                void post({
                  action: 'verify_otp',
                  phone: form.phone,
                  code: form.code,
                  name: form.name,
                  pin: form.pin || undefined,
                })
              }
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {mode === 'pin' && (
        <div className="space-y-2">
          <label className="input-label">Telemovel</label>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            inputMode="tel"
            autoComplete="tel"
          />
          <label className="input-label">PIN</label>
          <input
            className="input"
            value={form.pin}
            onChange={(e) => setForm({ ...form, pin: e.target.value })}
            inputMode="numeric"
            maxLength={6}
            autoComplete="current-password"
          />
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => setMode('idle')}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-primary flex-1"
              disabled={busy}
              onClick={() => void post({ action: 'login_pin', phone: form.phone, pin: form.pin })}
            >
              <UserRound className="h-4 w-4" /> Entrar
            </button>
          </div>
        </div>
      )}

      {message && <p className="mt-3 text-sm font-medium text-brand">{message}</p>}
      {error && <p className="mt-3 text-sm text-danger-text">{error}</p>}
    </section>
  );
}
